import { AuthApiService } from './AuthApiService';
import { RefreshTokenResponseType } from '../interfaces/Auth';

const REFRESH_LOCK_NAME = 'claimbox-token-refresh';

// The cross-tab source of truth is storage, not the current tab's Redux: the Redux copy can be stale (background tab, late adoption) and the token is one-time-use.
const readStoredRefreshToken = (): string | null => {
  const stored = localStorage.getItem('refresh_token');

  if (stored) {
    return stored;
  }

  return sessionStorage.getItem('safari_auth_refreshToken');
};

const writeStoredTokens = (token: string, refreshToken: string) => {
  localStorage.setItem('access_token', token);
  localStorage.setItem('refresh_token', refreshToken);
};

async function performSharedRefresh(
  fallbackRefreshToken?: string
): Promise<RefreshTokenResponseType | null> {
  // Re-read under the lock: another tab may have just rotated the token while we waited.
  const refreshToken = readStoredRefreshToken() || fallbackRefreshToken || null;

  if (!refreshToken) {
    return null;
  }

  const data = await AuthApiService.refreshToken(refreshToken)
    .then((response) => response.data ?? null)
    .catch(() => null);

  if (data && data.success) {
    writeStoredTokens(data.tokenResponse.access_token, data.tokenResponse.refresh_token);

    return data;
  }

  // The token we used failed: if storage meanwhile holds a DIFFERENT token (another tab completed the rotation), retry once with that one instead of dropping the session.
  const latestRefreshToken = readStoredRefreshToken();

  if (latestRefreshToken && latestRefreshToken !== refreshToken) {
    const retryData = await AuthApiService.refreshToken(latestRefreshToken)
      .then((response) => response.data ?? null)
      .catch(() => null);

    if (retryData && retryData.success) {
      writeStoredTokens(
        retryData.tokenResponse.access_token,
        retryData.tokenResponse.refresh_token
      );
    }

    return retryData;
  }

  return data;
}

// Single in-flight refresh: per-tab via the shared promise, cross-tab via Web Locks (falls back to the per-tab behavior where the API is unavailable).
let inFlightRefresh: Promise<RefreshTokenResponseType | null> | null = null;

export function refreshTokenShared(
  fallbackRefreshToken?: string
): Promise<RefreshTokenResponseType | null> {
  if (inFlightRefresh) {
    return inFlightRefresh;
  }

  const locks = typeof navigator !== 'undefined' ? (navigator as any).locks : undefined;

  const runRefresh: Promise<RefreshTokenResponseType | null> = locks
    ? locks.request(REFRESH_LOCK_NAME, () => performSharedRefresh(fallbackRefreshToken))
    : performSharedRefresh(fallbackRefreshToken);

  inFlightRefresh = runRefresh
    .catch(() => null)
    .finally(() => {
      inFlightRefresh = null;
    });

  return inFlightRefresh;
}
